import type { MerchantInboxMessage } from './merchantNotificationsApi.js';

export const MERCHANT_INBOX_RETENTION_MONTHS = 24;

export function merchantInboxCutoff(now = new Date()): Date {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - MERCHANT_INBOX_RETENTION_MONTHS);
  return cutoff;
}

export function isWithinMerchantInboxWindow(createdAt?: string | null, now = new Date()): boolean {
  if (!createdAt) return true;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return true;
  return t >= merchantInboxCutoff(now).getTime();
}

function trimString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function subjectFromDeepLink(deepLink?: string | null): string | null {
  if (!deepLink) return null;
  try {
    const url = new URL(deepLink, 'https://merchant.beatq.example');
    const orderPath = url.pathname.match(/\/orders\/([^/]+)/i);
    if (orderPath?.[1]) return `ORDER:${decodeURIComponent(orderPath[1])}`;
    const payoutPath = url.pathname.match(/\/payouts\/([^/]+)/i);
    if (payoutPath?.[1]) return `PAYOUT:${decodeURIComponent(payoutPath[1])}`;
    const queryOrder = url.searchParams.get('orderId') ?? url.searchParams.get('order');
    if (queryOrder) return `ORDER:${queryOrder}`;
  } catch {
    return null;
  }
  return null;
}

function messageTime(message: MerchantInboxMessage): number {
  const iso = trimString(message.createdAt) ?? trimString(message.created_at);
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

/** Grouping key from API `semantic_subject`, then subject, then deep-link, then category. */
export function merchantNotificationSubjectKey(message: MerchantInboxMessage): string {
  const semantic = trimString(message.semanticSubject) ?? trimString(message.semantic_subject);
  if (semantic) return semantic;
  if (trimString(message.subjectKind) && trimString(message.subjectId)) {
    return `${message.subjectKind}:${message.subjectId}`;
  }
  const fromLink = subjectFromDeepLink(message.deepLink ?? message.deep_link);
  if (fromLink) return fromLink;
  const category = trimString(message.category);
  if (category) return category;
  return 'Other';
}

export function inferMessageBranchId(message: MerchantInboxMessage): string | null {
  const explicit = trimString(message.branchId) ?? trimString(message.branch_id);
  if (explicit) return explicit;
  const deepLink = message.deepLink ?? message.deep_link;
  if (!deepLink) return null;
  try {
    const url = new URL(deepLink, 'https://merchant.beatq.example');
    return url.searchParams.get('branchId') ?? url.pathname.match(/\/br\/([^/]+)/)?.[1] ?? null;
  } catch {
    return null;
  }
}

export function merchantMessageBranchLabel(
  message: MerchantInboxMessage,
  branches: Array<{ id?: string; name?: string | null }>,
): string | null {
  const named = trimString(message.branchName);
  if (named) return named;
  const branchId = inferMessageBranchId(message);
  if (!branchId) return null;
  const match = branches.find((branch) => branch.id === branchId);
  return trimString(match?.name) ?? branchId;
}

export function groupMerchantNotifications(
  messages: MerchantInboxMessage[],
): Map<string, MerchantInboxMessage[]> {
  const groups = new Map<string, MerchantInboxMessage[]>();
  for (const item of messages) {
    const key = merchantNotificationSubjectKey(item);
    const bucket = groups.get(key) ?? [];
    bucket.push(item);
    groups.set(key, bucket);
  }
  for (const [key, items] of groups) {
    items.sort((a, b) => messageTime(b) - messageTime(a));
    groups.set(key, items);
  }
  return new Map(
    [...groups.entries()].sort(([, a], [, b]) => messageTime(b[0]!) - messageTime(a[0]!)),
  );
}

export function filterMerchantInbox(
  messages: MerchantInboxMessage[],
  opts: {
    branchId?: string | null;
    scopedBranchIds?: readonly string[];
    unreadOnly?: boolean;
    category?: string | null;
    roles?: readonly string[];
    now?: Date;
  },
): MerchantInboxMessage[] {
  const scoped = new Set(opts.scopedBranchIds ?? []);
  const floorOnly = isFloorOnlyMerchantActor(opts.roles ?? []);
  return messages.filter((message) => {
    if (
      opts.unreadOnly &&
      (message.isRead === true || message.is_read === true || message.read === true)
    ) {
      return false;
    }
    if (opts.category && (message.category ?? '') !== opts.category) return false;
    if (!isWithinMerchantInboxWindow(message.createdAt ?? message.created_at, opts.now))
      return false;
    const branchId = inferMessageBranchId(message);
    if (branchId && scoped.size > 0 && !scoped.has(branchId)) return false;
    if (opts.branchId) {
      if (!branchId || branchId !== opts.branchId) return false;
    }
    if (floorOnly && isBackOfficeMerchantNotice(message)) return false;
    return true;
  });
}

export function humanizeSubjectKey(key: string): string {
  if (key.startsWith('ORDER:')) return `Order ${key.slice(6)}`;
  if (key.startsWith('PAYOUT:')) return `Payout ${key.slice(7)}`;
  return key.replace(/_/g, ' ');
}

const OFFICE_ROLES = ['ROLE-MER-001', 'ROLE-MER-002', 'ROLE-MER-003', 'ROLE-MER-007'] as const;
const FLOOR_ROLES = ['ROLE-MER-004', 'ROLE-MER-005', 'ROLE-MER-006'] as const;
const FLOOR_EXCLUDED_NOTICE = /payout|settlement|finance|statement|invoice|export|instant/i;

/** 07-05: kitchen/cashier/picker see their function only — not payout notifications. */
export function isFloorOnlyMerchantActor(roles: readonly string[]): boolean {
  if (roles.length === 0) return false;
  if (roles.some((role) => (OFFICE_ROLES as readonly string[]).includes(role))) return false;
  return roles.some((role) => (FLOOR_ROLES as readonly string[]).includes(role));
}

export function isBackOfficeMerchantNotice(message: MerchantInboxMessage): boolean {
  const category = trimString(message.category) ?? '';
  const subject = merchantNotificationSubjectKey(message);
  const title = trimString(message.title) ?? '';
  return (
    FLOOR_EXCLUDED_NOTICE.test(category) ||
    FLOOR_EXCLUDED_NOTICE.test(subject) ||
    FLOOR_EXCLUDED_NOTICE.test(title)
  );
}

/** Merchant timestamps are Europe/London (16-02 / ASM-108). */
export function formatMerchantInboxTime(iso?: string | null, timeZone = 'Europe/London'): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleString('en-GB', {
    timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
